import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import api, { formatFechaLimite } from '../services/api';

const PersonalDashboard = () => {
  const { user } = useAuth();
  const { showSuccess, showError, showConfirm } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [partesAsignadas, setPartesAsignadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('partes');

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, partesResponse] = await Promise.all([
        api.get('/user/me/stats'),
        api.get('/partes/asignadas')
      ]);
      setStats(statsResponse.data);
      setPartesAsignadas(partesResponse.data.partes || partesResponse.data);
    } catch (error) {
      console.error('Error cargando datos del dashboard:', error);
      if (!loading) {
        showError('Error al cargar datos del dashboard. Por favor, recarga la página.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      PENDIENTE: { color: 'warning', text: 'Pendiente' },
      EN_PROGRESO: { color: 'info', text: 'En Progreso' },
      COMPLETADA: { color: 'success', text: 'Completada' }
    };
    const badge = badges[estado] || { color: 'secondary', text: estado };
    return (
      <span className={`badge badge-${badge.color}`} style={{
        backgroundColor: badge.color === 'warning' ? '#ffc107' : 
                        badge.color === 'info' ? '#17a2b8' : 
                        badge.color === 'success' ? '#28a745' : '#6c757d',
        color: badge.color === 'warning' ? '#212529' : 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '500'
      }}>
        {badge.text}
      </span>
    );
  };

  const getPrioridadBadge = (fechaLimite) => {
    const hoy = new Date();
    const limite = new Date(fechaLimite);
    const diasRestantes = Math.ceil((limite - hoy) / (1000 * 60 * 60 * 24));
    
    if (diasRestantes < 0) {
      return <span className="badge badge-danger" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>Vencida</span>;
    } else if (diasRestantes <= 3) {
      return <span className="badge badge-warning" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>Urgente</span>;
    } else if (diasRestantes <= 7) {
      return <span className="badge badge-info" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>Próxima</span>;
    } else {
      return <span className="badge badge-success" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>Normal</span>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="sr-only">Cargando dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="personal-dashboard-compact">
      {/* Header compacto */}
      <div className="dashboard-header-compact">
        <div className="header-content">
          <div>
            <h4 className="mb-0">¡Hola, {user?.nombre}!</h4>
            <small className="text-muted">Dashboard Personal</small>
          </div>
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={loadDashboardData}
            disabled={loading}
            style={{ height: '32px', padding: '0 12px' }}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Estadísticas compactas */}
      {stats && (
        <div className="stats-compact">
          <div className="stat-item">
            <div className="stat-icon" style={{ background: '#007bff' }}>
              <i className="fas fa-clipboard-list"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.partesAsignadas}</div>
              <div className="stat-label">Asignadas</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: '#28a745' }}>
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.partesCompletadas}</div>
              <div className="stat-label">Completadas</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: '#ffc107' }}>
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.partesPendientes}</div>
              <div className="stat-label">Pendientes</div>
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" style={{ background: '#17a2b8' }}>
              <i className="fas fa-tasks"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.partesEnProgreso}</div>
              <div className="stat-label">En Progreso</div>
            </div>
          </div>
        </div>
      )}

      {/* Barra de progreso compacta */}
      {stats && (
        <div className="progress-compact">
          <div className="progress-header">
            <span className="progress-title">
              <i className="fas fa-chart-line"></i> Progreso General
            </span>
            <span className="progress-value">{stats.porcentajeCompletado.toFixed(0)}%</span>
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar-fill"
              style={{
                width: `${stats.porcentajeCompletado}%`,
                background: stats.porcentajeCompletado >= 80 ? '#28a745' : 
                          stats.porcentajeCompletado >= 50 ? '#ffc107' : 
                          stats.porcentajeCompletado >= 25 ? '#fd7e14' : '#dc3545'
              }}
            ></div>
          </div>
          <div className="progress-info">
            <small className="text-muted">
              {stats.partesCompletadas} de {stats.partesAsignadas} completadas
            </small>
            <span className={`badge ${stats.porcentajeCompletado >= 80 ? 'bg-success' : 
              stats.porcentajeCompletado >= 50 ? 'bg-warning' : 
              stats.porcentajeCompletado >= 25 ? 'bg-orange' : 'bg-danger'}`}
              style={{ fontSize: '0.7rem', padding: '4px 8px' }}>
              {stats.porcentajeCompletado >= 80 ? 'Excelente' : 
               stats.porcentajeCompletado >= 50 ? 'Bien' : 
               stats.porcentajeCompletado >= 25 ? 'Regular' : 'Urgente'}
            </span>
          </div>
        </div>
      )}

      {/* Pestañas compactas */}
      <div className="tabs-compact">
        <button
          className={`tab-btn ${activeTab === 'partes' ? 'active' : ''}`}
          onClick={() => setActiveTab('partes')}
        >
          <i className="fas fa-list"></i> Pendientes
        </button>
        <button
          className={`tab-btn ${activeTab === 'urgentes' ? 'active' : ''}`}
          onClick={() => setActiveTab('urgentes')}
        >
          <i className="fas fa-exclamation-triangle"></i> Urgentes
        </button>
        <button
          className={`tab-btn ${activeTab === 'completadas' ? 'active' : ''}`}
          onClick={() => setActiveTab('completadas')}
        >
          <i className="fas fa-check"></i> Completadas
        </button>
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-chart-bar"></i> Resumen
        </button>
      </div>

      {/* Contenido de las pestañas */}
      <div className="tab-content-compact">
        {activeTab === 'partes' && (
          <div>
            {partesAsignadas.filter(p => p.estado !== 'COMPLETADA').length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-check-circle"></i>
                <p>¡Excelente! No tienes partes pendientes.</p>
              </div>
            ) : (
              <div className="table-compact-wrapper">
                <table className="table-compact">
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Expediente</th>
                      <th>Estado</th>
                      <th>Fecha Límite</th>
                      <th>Prioridad</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partesAsignadas
                      .filter(parte => parte.estado !== 'COMPLETADA')
                      .sort((a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite))
                      .map(parte => (
                        <tr key={parte.id}>
                          <td><strong>{parte.descripcion}</strong></td>
                          <td>
                            <div className="expediente-info">
                              <span className="expediente-num">#{parte.expediente?.numero}</span>
                              <small>{parte.expediente?.cliente?.nombre}</small>
                            </div>
                          </td>
                          <td>{getEstadoBadge(parte.estado)}</td>
                          <td><small>{formatFechaLimite(parte.fechaLimite)}</small></td>
                          <td>{getPrioridadBadge(parte.fechaLimite)}</td>
                          <td>
                            <button 
                              className="btn-icon"
                              onClick={() => navigate(`/mis-partes-asignadas/${parte.id}`)}
                              title="Ver detalles"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'urgentes' && (
          <div>
            {partesAsignadas
              .filter(p => {
                const limite = new Date(p.fechaLimite);
                const hoy = new Date();
                const diasRestantes = Math.ceil((limite - hoy) / (1000 * 60 * 60 * 24));
                return diasRestantes <= 7 && p.estado !== 'COMPLETADA';
              })
              .length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-thumbs-up"></i>
                <p>No tienes partes urgentes actualmente.</p>
              </div>
            ) : (
              <div className="table-compact-wrapper">
                <table className="table-compact">
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Expediente</th>
                      <th>Estado</th>
                      <th>Vence</th>
                      <th>Días</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partesAsignadas
                      .filter(p => {
                        const limite = new Date(p.fechaLimite);
                        const hoy = new Date();
                        const diasRestantes = Math.ceil((limite - hoy) / (1000 * 60 * 60 * 24));
                        return diasRestantes <= 7 && p.estado !== 'COMPLETADA';
                      })
                      .sort((a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite))
                      .map(parte => {
                        const limite = new Date(parte.fechaLimite);
                        const hoy = new Date();
                        const diasRestantes = Math.ceil((limite - hoy) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <tr key={parte.id} className={diasRestantes < 0 ? 'row-danger' : ''}>
                            <td><strong>{parte.descripcion}</strong></td>
                            <td>
                              <div className="expediente-info">
                                <span className="expediente-num">#{parte.expediente?.numero}</span>
                                <small>{parte.expediente?.cliente?.nombre}</small>
                              </div>
                            </td>
                            <td>{getEstadoBadge(parte.estado)}</td>
                            <td><small>{formatFechaLimite(parte.fechaLimite)}</small></td>
                            <td>
                              <span className={`badge ${diasRestantes < 0 ? 'badge-danger' : 'badge-warning'}`} style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                                {diasRestantes < 0 ? 'Vencida' : `${diasRestantes}d`}
                              </span>
                            </td>
                            <td>
                              <button 
                                className="btn-icon"
                                onClick={() => navigate(`/mis-partes-asignadas/${parte.id}`)}
                                title="Ver detalles"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'completadas' && (
          <div>
            {partesAsignadas.filter(p => p.estado === 'COMPLETADA').length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-clock"></i>
                <p>Aún no has completado ninguna parte.</p>
              </div>
            ) : (
              <div className="table-compact-wrapper">
                <table className="table-compact">
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Expediente</th>
                      <th>Límite</th>
                      <th>Completado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partesAsignadas
                      .filter(parte => parte.estado === 'COMPLETADA')
                      .sort((a, b) => new Date(b.fechaCompletado || 0) - new Date(a.fechaCompletado || 0))
                      .map(parte => (
                        <tr key={parte.id} className="row-completed">
                          <td>
                            <i className="fas fa-check-circle text-success me-1"></i>
                            <strong>{parte.descripcion}</strong>
                          </td>
                          <td>
                            <div className="expediente-info">
                              <span className="expediente-num">#{parte.expediente?.numero}</span>
                              <small>{parte.expediente?.cliente?.nombre}</small>
                            </div>
                          </td>
                          <td><small>{formatFechaLimite(parte.fechaLimite)}</small></td>
                          <td>
                            <small className="text-success">
                              {parte.fechaCompletado ? new Date(parte.fechaCompletado).toLocaleDateString() : 'N/A'}
                            </small>
                          </td>
                          <td>
                            <button 
                              className="btn-icon"
                              onClick={() => navigate(`/mis-partes-asignadas/${parte.id}`)}
                              title="Ver detalles"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="overview-card">
              <h6><i className="fas fa-chart-bar"></i> Estadísticas</h6>
              <div className="stats-list">
                <div className="stats-row">
                  <span>Total de partes:</span>
                  <strong>{partesAsignadas.length}</strong>
                </div>
                <div className="stats-row">
                  <span>Pendientes:</span>
                  <strong className="text-warning">{partesAsignadas.filter(p => p.estado !== 'COMPLETADA').length}</strong>
                </div>
                <div className="stats-row">
                  <span>Completadas:</span>
                  <strong className="text-success">{partesAsignadas.filter(p => p.estado === 'COMPLETADA').length}</strong>
                </div>
                <div className="stats-row">
                  <span>Vencidas:</span>
                  <strong className="text-danger">
                    {partesAsignadas.filter(p => new Date(p.fechaLimite) < new Date() && p.estado !== 'COMPLETADA').length}
                  </strong>
                </div>
              </div>
            </div>
            
            <div className="overview-card">
              <h6><i className="fas fa-calendar-alt"></i> Próximas Fechas</h6>
              <div className="upcoming-list">
                {partesAsignadas
                  .filter(p => p.estado !== 'COMPLETADA')
                  .sort((a, b) => new Date(a.fechaLimite) - new Date(b.fechaLimite))
                  .slice(0, 4)
                  .map(parte => (
                    <div key={parte.id} className="upcoming-item">
                      <div>
                        <strong>{parte.descripcion}</strong>
                        <small>{formatFechaLimite(parte.fechaLimite)}</small>
                      </div>
                      {getPrioridadBadge(parte.fechaLimite)}
                    </div>
                  ))
                }
              </div>
            </div>

            <div className="overview-card">
              <h6><i className="fas fa-check-double"></i> Últimas Completadas</h6>
              <div className="completed-list">
                {partesAsignadas
                  .filter(p => p.estado === 'COMPLETADA')
                  .sort((a, b) => new Date(b.fechaCompletado || 0) - new Date(a.fechaCompletado || 0))
                  .slice(0, 4)
                  .map(parte => (
                    <div key={parte.id} className="completed-item">
                      <strong>{parte.descripcion}</strong>
                      <small className="text-success">
                        {parte.fechaCompletado ? new Date(parte.fechaCompletado).toLocaleDateString() : 'N/A'}
                      </small>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .personal-dashboard-compact {
          padding: 16px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .dashboard-header-compact {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 8px;
          padding: 16px 20px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: white;
        }

        .dashboard-header-compact h4 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }

        .stats-compact {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .stat-item {
          background: white;
          border-radius: 8px;
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          transition: transform 0.2s;
        }

        .stat-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1;
          margin-bottom: 4px;
          color: #212529;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #6c757d;
          font-weight: 500;
        }

        .progress-compact {
          background: white;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .progress-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #495057;
        }

        .progress-value {
          font-size: 1.125rem;
          font-weight: 700;
          color: #212529;
        }

        .progress-bar-wrapper {
          background: #e9ecef;
          height: 12px;
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 8px;
        }

        .progress-bar-fill {
          height: 100%;
          transition: width 0.5s ease;
          border-radius: 6px;
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .tabs-compact {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .tab-btn {
          flex: 1;
          min-width: 140px;
          padding: 10px 16px;
          border: none;
          background: white;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #6c757d;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .tab-btn:hover {
          background: #f8f9fa;
          transform: translateY(-1px);
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .tab-btn i {
          margin-right: 6px;
        }

        .tab-content-compact {
          background: white;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .empty-state {
          text-align: center;
          padding: 48px 24px;
          color: #6c757d;
        }

        .empty-state i {
          font-size: 3rem;
          margin-bottom: 16px;
          color: #28a745;
        }

        .empty-state p {
          font-size: 1rem;
          margin: 0;
        }

        .table-compact-wrapper {
          overflow-x: auto;
        }

        .table-compact {
          width: 100%;
          font-size: 0.875rem;
          border-collapse: collapse;
        }

        .table-compact thead {
          background: #f8f9fa;
        }

        .table-compact th {
          padding: 10px 12px;
          text-align: left;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
        }

        .table-compact td {
          padding: 12px;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
        }

        .table-compact tbody tr:hover {
          background: #f8f9fa;
        }

        .table-compact tbody tr.row-danger {
          background: #fff5f5;
        }

        .table-compact tbody tr.row-completed {
          background: #f8fff8;
        }

        .expediente-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .expediente-num {
          font-weight: 600;
          color: #212529;
        }

        .expediente-info small {
          color: #6c757d;
          font-size: 0.75rem;
        }

        .btn-icon {
          width: 32px;
          height: 32px;
          padding: 0;
          border: 1px solid #dee2e6;
          background: white;
          border-radius: 4px;
          color: #495057;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-icon:hover {
          background: #007bff;
          color: white;
          border-color: #007bff;
        }

        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }

        .overview-card {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 16px;
          border: 1px solid #e9ecef;
        }

        .overview-card h6 {
          font-size: 0.9rem;
          font-weight: 600;
          margin: 0 0 12px 0;
          color: #495057;
        }

        .overview-card h6 i {
          margin-right: 6px;
          color: #667eea;
        }

        .stats-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stats-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .stats-row:last-child {
          border-bottom: none;
        }

        .upcoming-list, .completed-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .upcoming-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 10px;
          background: white;
          border-radius: 6px;
          border-left: 3px solid #ffc107;
        }

        .upcoming-item > div {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .upcoming-item strong {
          font-size: 0.85rem;
          color: #212529;
        }

        .upcoming-item small {
          font-size: 0.75rem;
          color: #6c757d;
        }

        .completed-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: white;
          border-radius: 6px;
          border-left: 3px solid #28a745;
        }

        .completed-item strong {
          font-size: 0.85rem;
          color: #212529;
        }

        .completed-item small {
          font-size: 0.75rem;
        }

        @media (max-width: 768px) {
          .personal-dashboard-compact {
            padding: 12px;
          }

          .stats-compact {
            grid-template-columns: 1fr;
          }

          .tabs-compact {
            flex-direction: column;
          }

          .tab-btn {
            width: 100%;
          }

          .overview-grid {
            grid-template-columns: 1fr;
          }
        }

        .bg-orange {
          background-color: #fd7e14 !important;
          color: white !important;
        }
      `}</style>
    </div>
  );
};

export default PersonalDashboard;
